import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { TabView, TabPanel } from 'primereact/tabview';
import PromissoryNoteService from '../../services/PromissoryNoteService';

export default function PromissoryNoteListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const toast = useRef(null);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const res = await PromissoryNoteService.getPromissoryNotes();
            setNotes(res.data || []);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Senetler yüklenirken hata oluştu' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, []);

    const updateStatus = async (id, newStatus) => {
        try {
            const res = await PromissoryNoteService.updateStatus({ promissoryNoteId: id, newStatus });
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Senet durumu güncellendi' });
                fetchNotes();
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Durum güncellenirken hata oluştu' });
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statuses = {
            1: { label: 'Portföyde', severity: 'info' },
            2: { label: 'Ciro Edildi', severity: 'warning' },
            3: { label: 'Tahsil Edildi', severity: 'success' },
            4: { label: 'Ödendi', severity: 'success' },
            5: { label: 'İade Edildi', severity: 'danger' }
        };
        const status = statuses[rowData.status] || { label: 'Bilinmiyor', severity: 'secondary' };
        return <Tag value={status.label} severity={status.severity} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                {rowData.status === 1 ? (
                    <>
                        {rowData.type === 1 && <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text" tooltip="Tahsil Edildi" onClick={() => updateStatus(rowData.id, 3)} />}
                        {rowData.type === 2 && <Button icon="pi pi-money-bill" className="p-button-rounded p-button-success p-button-text" tooltip="Ödendi Olarak İşaretle" onClick={() => updateStatus(rowData.id, 4)} />}
                        <Button icon="pi pi-replay" className="p-button-rounded p-button-danger p-button-text" tooltip="İade Et" onClick={() => updateStatus(rowData.id, 5)} />
                    </>
                ) : (
                    <Button icon="pi pi-undo" className="p-button-rounded p-button-warning p-button-text" tooltip="Geri Al (Portföye)" onClick={() => updateStatus(rowData.id, 1)} />
                )}
            </div>
        );
    };

    const inboundNotes = notes.filter(x => x.type === 1);
    const outboundNotes = notes.filter(x => x.type === 2);

    const renderHeader = () => {
        return (
            <div className="flex justify-content-end">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Ara..."
                        className="p-inputtext-sm !py-2.5 !rounded-xl"
                    />
                </span>
            </div>
        );
    };

    const header = renderHeader();

    return (
        <div className="p-4 space-y-4 animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
            <div className="bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="!bg-orange-600 p-4 rounded-xl shadow-lg shadow-orange-600/20">
                            <i className="pi pi-file text-white text-xl"></i>
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-800 m-0">Senet Listesi</h1>
                            <p className="text-xs text-slate-500 font-medium">Alınan ve verilen tüm senetlerin takibi</p>
                        </div>
                    </div>
                </div>

                <TabView className="compact-tabview">
                    <TabPanel header="Müşteriden Alınan Senetler" leftIcon="pi pi-arrow-down-left mr-2">
                        <DataTable value={inboundNotes} loading={loading} paginator rows={10} globalFilter={globalFilter} header={header}
                            responsiveLayout="scroll" className="p-datatable-sm mt-2">
                            <Column field="issueDate" header="Alınış Tarihi" body={(row) => new Date(row.issueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column field="dueDate" header="Vade" body={(row) => new Date(row.dueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column field="customerName" header="Müşteri" sortable />
                            <Column field="supplierName" header="Ciro Edilen Tedarikçi" sortable />
                            <Column field="drawer" header="Borçlu" sortable />
                            <Column field="amount" header="Tutar" body={(row) => `₺${row.amount.toLocaleString('tr-TR')}`} sortable />
                            <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                            <Column header="İşlem" body={actionBodyTemplate} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Verilen Senetler" leftIcon="pi pi-arrow-up-right mr-2">
                        <DataTable value={outboundNotes} loading={loading} paginator rows={10} globalFilter={globalFilter} header={header}
                            responsiveLayout="scroll" className="p-datatable-sm mt-2">
                            <Column field="issueDate" header="Veriliş Tarihi" body={(row) => new Date(row.issueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column field="dueDate" header="Vade" body={(row) => new Date(row.dueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column header="Alıcı (Tedarikçi/Müşteri)" body={(row) => row.supplierName || row.customerName || '-'} sortable sortField="supplierName" />
                            <Column field="drawer" header="Borçlu" sortable />
                            <Column field="amount" header="Tutar" body={(row) => `₺${row.amount.toLocaleString('tr-TR')}`} sortable />
                            <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                            <Column header="İşlem" body={actionBodyTemplate} />
                        </DataTable>
                    </TabPanel>
                </TabView>
            </div>
        </div>
    );
}
